import { AsyncResult, ApiAction } from '../index';
import api from '../redux-api';
import { USAGE_TYPES, ComparisonItemEvent, PortfolioHoldingEvent, serializeComparisonItem } from '../../utils/usage-log';
import { getCurrentStateComparison } from '../../utils/comparison';
import sentryUtils from '../../utils/3rd-party/sentry';
import ComparisonItem from '@/types/comparison-item';
import RiskProfileItem from '@/types/risk-profile-item';
import PortfolioHolding from '@/types/portfolio-holding';
import Portfolio from '@/types/portfolio';
import createBatchLogger from '@/utils/batch-logger';

// Actions
const ADD_USAGE: ApiAction = {
  type: 'connect/usage-log/add-usage',
  endpoint: 'usage-logs/',
};

const postBatch = (logs: { type: string; values: any }[]) => api.post(ADD_USAGE, null, { logs });

const sendUsageLog = createBatchLogger(postBatch);

// Action Creators
export const logNewComparison = (): AsyncResult<void> => async () => {
  const body = {
    type: USAGE_TYPES.NEW_COMPARISON,
    values: {},
  };

  return sendUsageLog(body);
};

const logComparisonItemEvent =
  (type: ComparisonItemEvent, ...items: ComparisonItem[]): AsyncResult<void> =>
  async (dispatch, getState) => {
    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison) {
      return;
    }

    let comparisonId = comparison._id;
    if (comparisonId && comparisonId.startsWith('new-')) {
      comparisonId = undefined;
    }

    const promises = items.map(item => {
      if (item._id.startsWith('new-')) {
        return Promise.resolve();
      }

      const body = {
        type,
        values: {
          comparisonId,
          comparisonCorrelationId: comparison.correlationId,
          ...serializeComparisonItem(item),
        },
      };

      return sendUsageLog(body);
    });

    Promise.all(promises).catch(sentryUtils.captureError);
  };

export const logAddToComparison = (...items: ComparisonItem[]): AsyncResult<void> => logComparisonItemEvent(USAGE_TYPES.ADD_TO_COMPARISON, ...items);

export const logRemoveFromComparison = (...items: ComparisonItem[]): AsyncResult<void> => logComparisonItemEvent(USAGE_TYPES.REMOVE_FROM_COMPARISON, ...items);

export const logAttachRiskProfile =
  (profile: RiskProfileItem): AsyncResult<void> =>
  async (dispatch, getState) => {
    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison) {
      return;
    }

    let comparisonId = comparison._id;
    if (comparisonId && comparisonId.startsWith('new-')) {
      comparisonId = undefined;
    }

    const body = {
      type: USAGE_TYPES.ATTACH_RISK_PROFILE_TO_COMPARISON,
      values: {
        comparisonId,
        comparisonCorrelationId: comparison.correlationId,
        riskProfileId: profile._id,
        riskProfileTitle: profile.title['en-CA'],
      },
    };

    return sendUsageLog(body);
  };

export const logNewPortfolio = (): AsyncResult<void> => async () => {
  const body = {
    type: USAGE_TYPES.NEW_PORTFOLIO,
    values: {},
  };

  return sendUsageLog(body);
};

const serializeHolding = (holding: PortfolioHolding) => ({
  type: holding.original.type,
  symbol: holding.symbol,
  name: holding.name['en-CA'],
});

export const logPortfolioHoldingEvent =
  (type: PortfolioHoldingEvent, holding: PortfolioHolding, portfolio: Portfolio): AsyncResult<void> =>
  async () => {
    let portfolioId = portfolio._id;
    if (portfolioId && portfolioId.startsWith('new-')) {
      portfolioId = undefined;
    }

    const body = {
      type,
      values: {
        portfolioId,
        portfolioCorrelationId: portfolio.correlationId,
        ...serializeHolding(holding),
      },
    };

    return sendUsageLog(body);
  };

export const logAddToPortfolio = (holding: PortfolioHolding, portfolio: Portfolio): AsyncResult<void> =>
  logPortfolioHoldingEvent(USAGE_TYPES.ADD_TO_PORTFOLIO, holding, portfolio);

export const logRemoveFromPortfolio = (holding: PortfolioHolding, portfolio: Portfolio): AsyncResult<void> =>
  logPortfolioHoldingEvent(USAGE_TYPES.REMOVE_FROM_PORTFOLIO, holding, portfolio);

export const logAddComparisonMarkup =
  (category: string, field: string, type: string): AsyncResult<void> =>
  async (dispatch, getState) => {
    const comparison = getCurrentStateComparison(getState().comparison);

    if (!comparison) {
      return;
    }

    let comparisonId = comparison._id;
    if (comparisonId && comparisonId.startsWith('new-')) {
      comparisonId = undefined;
    }

    const body = {
      type: USAGE_TYPES.ADD_MARKUP,
      values: {
        comparisonId,
        comparisonCorrelationId: comparison.correlationId,
        category,
        field,
        type,
      },
    };

    return sendUsageLog(body);
  };

export const logResetComparisonMarkup = (): AsyncResult<void> => async (dispatch, getState) => {
  const comparison = getCurrentStateComparison(getState().comparison);

  if (!comparison) {
    return;
  }

  let comparisonId = comparison._id;
  if (comparisonId && comparisonId.startsWith('new-')) {
    comparisonId = undefined;
  }

  const body = {
    type: USAGE_TYPES.RESET_MARKUP,
    values: {
      comparisonId,
      comparisonCorrelationId: comparison.correlationId,
    },
  };

  return sendUsageLog(body);
};

export const logOpenHelpCenter = (): AsyncResult<void> => async () => {
  const body = {
    type: USAGE_TYPES.OPEN_HELP_CENTER,
    values: {},
  };

  return sendUsageLog(body);
};
